// Mod_Building.ts — the top-level Building modifier (Buildings Set1, §2.2).
//
// A Building object is the anchor of a building tree: it owns the `BuildingDefaults` group that
// every descendant Room/Window/Door falls back through, and (unless it is a prototype via
// `fallbackFor`) it is the root that generates the building's floors/walls/roofs. Geometry is
// intentionally NOT in P1 — this phase wires the data model + the §3 fallback engine only.
import {BuildingMaterials, DoorSwing, RoofStyle} from "./Building/BuildingShared.js";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export enum BuildingFallbackFor { global = "global", descendants = "descendants" }

export class Mod_Building {
	constructor(data?: Partial<Mod_Building>) { Object.assign(this, data); }

	/** Non-null => this modifier is INERT (generates no meshes); it exists only as a prototype
	 *  that other Building modifiers read null-field defaults from. */
	fallbackFor?: BuildingFallbackFor|n;

	/** When false, the roof generator is skipped for this building (walls/floors still build). */
	roofEnabled?: boolean|n;

	/** All inheritable defaults, grouped by the modifier that consumes them. Every leaf is
	 *  nullable; a null leaf simply doesn't participate in the fallback chain. */
	defaults = new BuildingDefaults({});
}

/** The grouped inheritable defaults owned by a `Mod_Building`. Grouping the ~25 inheritable
 *  fields under these sub-objects (instead of flat on `Mod_Building`) is what makes the §3
 *  fallback merge a single generic routine instead of 25 hand-written `??` chains. */
export class BuildingDefaults {
	constructor(data?: Partial<BuildingDefaults>) { Object.assign(this, data); }
	level = new LevelDefaults({});
	room = new RoomDefaults({});
	window = new WindowDefaults({});
	door = new DoorDefaults({});
	opening = new OpeningDefaults({});
	roof = new RoofDefaults({});
	materials = new BuildingMaterials({});
}
export class LevelDefaults  {
	constructor(data?: Partial<LevelDefaults>) { Object.assign(this, data); }
	floorThickness?: number|n; wallHeight?: number|n;
}
export class RoomDefaults   {
	constructor(data?: Partial<RoomDefaults>) { Object.assign(this, data); }
	wallThickness?: number|n; hasFloor?: boolean|n; hasCeiling?: boolean|n;
}
export class WindowDefaults {
	constructor(data?: Partial<WindowDefaults>) { Object.assign(this, data); }
	width?: number|n; height?: number|n; sillHeight?: number|n; paneDivisionsX?: number|n; paneDivisionsY?: number|n;
}
export class DoorDefaults   {
	constructor(data?: Partial<DoorDefaults>) { Object.assign(this, data); }
	width?: number|n; height?: number|n; swing?: DoorSwing|n; openAngle?: number|n;
}
export class OpeningDefaults{
	constructor(data?: Partial<OpeningDefaults>) { Object.assign(this, data); }
	width?: number|n;
}
export class RoofDefaults   {
	constructor(data?: Partial<RoofDefaults>) { Object.assign(this, data); }
	style?: RoofStyle|n;
	/** When `style == gable`, default the ridge along the LONG axis; set to flip it to the SHORT axis. */
	gableAlongShorter?: boolean|n;
	pitch?: number|n; eaveOverhang?: number|n; fasciaDepth?: number|n;
	soffitEnabled?: boolean|n; ridgeHeightMax?: number|n; latticeStep?: number|n;
}

// Re-export for convenience so consumers can import from a single file.
export {BuildingMaterials, RoofStyle} from "./Building/BuildingShared.js";
